import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HardHat } from "@phosphor-icons/react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1742415106102-77bbfe14b872?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50JTIwbW9kZXJufGVufDB8fHx8MTc4NTA0MTIxMXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative bg-[hsl(var(--blueprint))]">
        <img src={BG} alt="Blueprint" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 bg-primary flex items-center justify-center">
              <HardHat size={22} weight="fill" />
            </div>
            <span className="font-heading font-black text-xl tracking-tighter">CONSTRUCT<span className="text-primary">HUB</span></span>
          </div>
          <div>
            <div className="overline text-primary mb-3">Holiday Inn Express · Lansing, KS</div>
            <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-none">
              Build the entire<br />project lifecycle.
            </h1>
            <p className="mt-4 text-white/60 max-w-md">
              From pre-construction ownership setup through general contractor bidding — one control room for the whole team.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-6 animate-fade-up" data-testid="login-form">
          <div>
            <div className="overline text-primary mb-2">Ownership Portal</div>
            <h2 className="font-heading font-black text-3xl tracking-tighter">Sign in</h2>
            <p className="text-muted-foreground text-sm mt-1">Access your construction command center.</p>
          </div>

          {error && (
            <div className="border border-destructive/50 bg-destructive/10 text-destructive text-sm px-4 py-3" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="overline">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              data-testid="login-email-input" className="rounded-none h-11 focus:ring-2 focus:ring-primary/50 focus:border-primary" placeholder="you@company.com" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="overline">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              data-testid="login-password-input" className="rounded-none h-11 focus:ring-2 focus:ring-primary/50 focus:border-primary" placeholder="••••••••" />
          </div>

          <Button type="submit" disabled={busy} data-testid="login-submit-button"
            className="w-full rounded-none h-11 bg-primary hover:bg-primary/90 text-white font-medium transition-transform duration-150 hover:-translate-y-[1px]">
            {busy ? "Signing in…" : "Sign In"}
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            New owner?{" "}
            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="goto-register-link">
              Create ownership account
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
